import { DELI_ENV_BASE_URL, http, http_v3 } from '@api/client';
import * as designationService from '@api/person/designation';
import { AppNoBodyPadding } from '@components/app-settings';
import { useFormSnackbarError } from '@lib/common';
import { CompanyDetail } from '@lib/company-mapping';
import { AppBar, Box, FormControl, Grid, IconButton, InputLabel, Select, TextField, Tooltip, Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { blue, purple } from '@material-ui/core/colors';
import { Autocomplete } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { ControlledTextField } from 'pages/person-management/person-management-form/person-master-relation-form';
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { map } from 'rxjs/operators';
import styled from 'styled-components';
import { debounce } from 'lodash';
import ClipBoard, { SingleBuyerSellerCell } from '../search/columns/utils';
import { FormActions } from './form-actions';
import * as adapter from './form-adapter';
import { C3Type } from '../add/types';
import CopyClipboard from '@material-ui/icons/AttachFile';


type C3FormProps = {
  onSubmit: (company: C3Type) => any;
  onDelete?: (company: C3Type) => any;
  c3Data: C3Type;
  createCompany?: boolean;
  c3RelationId?:string;
  redirectToSearch : () => void;
};

export function C3EditForm({
  c3Data,
  onSubmit,
  onDelete = null,
  createCompany = false,
  c3RelationId,
  redirectToSearch = ()=>{}
}: C3FormProps) {
  const c3FormData = useMemo(
    () => adapter.toForm(c3Data),
    [c3Data]
  );

  const form = useForm({
    defaultValues: c3FormData,
    // resolver: vestResolver(dealValidationSuite),
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    form.watch("role");
    form.watch("sub_role")
    form.watch('source_links')
    form.register("person_vtr_id")
    form.register("content_vtr_id")
  }, [])

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const formError = useFormSnackbarError();

  const submitForm = form.handleSubmit(onSubmit, formError);
  useEffect(() => {
    if (form.errors && form.errors['company2_duplicate']) {
      const company2 = form.watch('company2');
      if (company2) {
        const company2_names = (company2 as unknown as CompanyDetail[]).map(x => x.name);
        const duplicate_company2s = company2_names.filter(
          (x, idx) => company2_names.indexOf(x) != idx
        );
        enqueueSnackbar(`${duplicate_company2s.join(', ')} in Company-2's are duplicated`, {
          variant: 'warning',
        });
      }
    }
  }, [form.errors]);

  const C3MappingAppBar = () => {
    return (
      <AppBar color="secondary" style={{ position: 'sticky', top: 64, background: purple[800] }}>
        <Grid
          container
          justify="flex-end"
          alignItems="flex-end"
          spacing={2}
          style={{ margin: 0, width: '100%' }}>
          <Grid item>
            <FormActions
              submitForm={submitForm}
              redirectToSearch={redirectToSearch}
              c3FormData={c3FormData}
              vtr_id = {c3RelationId}
            />
          </Grid>
        </Grid>
      </AppBar>
    );
  };

  const person_master = c3Data?.person_vtr_id;

  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Debounced function to fetch role options
  const debouncedSearchRoles = useCallback(
    debounce((searchTerm: string) => {
      setLoading(true);
      http
        .get('/person-role')
        .pipe(
          map(res => res.data)
        )
        .subscribe({
          next: data => {
            let rolesArray = [];
            if (Array.isArray(data)) {
              rolesArray = data;
            } else if (data && Array.isArray(data.data)) {
              rolesArray = data.data;
            } else if (data && typeof data === 'object') {
              // If it's an object, try to extract array from common properties
              rolesArray = data.roles || data.items || data.results || [];
            }
            setOptions(rolesArray);
            form.setValue("sub_role","")
          },
          error: err => {
            console.error(err, 'Error fetching roles');
            setOptions([]); // Set empty array on error
          },
          complete: () => {
            setLoading(false);
          },
        });
    }, 300),
    []
  );

  useEffect(() => {
    // Initial load - fetch all roles
    debouncedSearchRoles('');
  }, [debouncedSearchRoles]);

  const [subRoleOptions, setSubRoleOptions] = useState<any[]>([]);
  const [subRoleLoading, setSubRoleLoading] = useState(false);

  // ================= SUB ROLE =====================
  const debouncedSearchSubRoles = useCallback(
    debounce((roleName: string, searchTerm: string) => {
      if (!roleName) return;
      setSubRoleLoading(true);
      http.get("/person-subrole")
        .pipe(map((res) => res.data))
        .subscribe({
        next: (data) => {
  let subRolesMap: Record<string, string[]> = {};

  if (data && typeof data === "object") {
    // Assuming API returns an object with role names as keys
    subRolesMap = data.data;
  } else if (data && Array.isArray(data.data)) {
              subRolesMap = data.data;
            }

  // `selectedRole` is the role chosen by the user
  const subRoleList = roleName ? subRolesMap[roleName] ?? [] : [];
  setSubRoleOptions(subRoleList);
},
          error: (err) => {
            console.error(err, "Error fetching sub-roles");
            setSubRoleOptions([]);
          },
          complete: () => setSubRoleLoading(false),
        });
    }, 300),
    []
  );

  // Refetch subroles when a new role is selected
  useEffect(() => {
    const selectedRole = form.getValues("role");
    
    if (selectedRole) {
      debouncedSearchSubRoles(selectedRole, "");
    } else {
      setSubRoleOptions([]);
      form.setValue("sub_role", "");
    }
  }, [form.watch("role")]);
  
  return (
    <>
      <AppNoBodyPadding />
      <div>
        <FormProvider {...form}>
          <form onSubmit={submitForm}>

            <C3MappingAppBar />
            <FormDiv>
              <Grid container xs={12} spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="h5" gutterBottom>
                   Person Name
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {c3Data?.person_name ? c3Data?.person_name : ''}
                  </Typography>
                </Grid>
                <br />
                <br />
                 <Grid item xs={1}>
                  <Tooltip title="Content">
                    <IconButton style={{ color: 'white' }} >
                      <img src="/images/content_default.svg"/>
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  
                  <CellForContentEntity contentName={c3Data?.content_title} contentCharacter={c3Data?.character ?? 'NA'} contentVtrId = {c3Data?.content_vtr_id} />
                </Grid>
                <br />
                <br />

              </Grid>
              <br />
              <br />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography variant="h6" gutterBottom>
                    Role
                  </Typography>
                 
                      <Autocomplete
                        options={options || []}
                        ref={form.register("role") as any}
                        value={form.getValues("role")}
                        onChange={(e,v)=>{
                          form.setValue('role',v)
                        }}
                        freeSolo
                        onInputChange={(e,v)=>{
                          if (e?.type === 'change') { 
                            form.setValue('role',v);
                            debouncedSearchRoles(v);
                          }
                        }}
                        renderInput={props => (
                          <TextField
                            label="Role"
                            fullWidth
                            variant="outlined"
                            {...props}
                            InputProps={{
                              ...props.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loading ? (
                                    <>
                                      <CircularProgress color="inherit" size={20} />
                                      {props.InputProps.endAdornment}
                                    </>
                                  ) : null}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                </Grid>

                <Grid item xs={8}>
               
                {/* SUB ROLE DROPDOWN */}
      {form.getValues("role") && (
        <Grid item xs={8}>
          <Typography variant="h6" gutterBottom>
            Sub Role
          </Typography>
          <Autocomplete
            options={subRoleOptions || []}
            ref={form.register("sub_role") as any}
            value={form.getValues("sub_role")}
            onChange={(e, v) => form.setValue("sub_role", v)}
            freeSolo
            onInputChange={(e, v) => {
              if (e?.type === "change") {
                form.setValue("sub_role", v);
                debouncedSearchSubRoles(form.getValues("role"), v);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub Role"
                fullWidth
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {subRoleLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}   </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Sources Link
                  </Typography>
                  <ControlledTextField
                    fullWidth
                    variant="outlined"
                    name={'source_links'}
                    label={'Sources Link'}
                    control={form.control}
                    defaultValue={c3FormData?.source_links ?? ''}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
              <br />
              <br />
              <Grid style={{ position: 'relative' }} container spacing={3}>
              {/*   {person_id && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    TMDB
                  </Typography>
                  <Tooltip title="TMDB">
                    <a target="_blank" style={{ display: "flex" }} href={`https://www.themoviedb.org/person/${person_id}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden",   }} src="/images/tmdb.jpeg" />
                    </a>
                  </Tooltip>
                </Grid>)}
                {linkedin_url && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    LinkedIn URL
                  </Typography>
                  <Tooltip title="Linkedin">
                    <a target="_blank" style={{ display: "flex" }} href={`${linkedin_url}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden",   }} src="/images/linkedin.png" />
                    </a>
                  </Tooltip>
                </Grid>)}
                {hubspot_id && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    Hubspot Id
                  </Typography>
                  <Tooltip title="Hubspot">
                    <a target="_blank" style={{ display: "flex" }} href={`https://app.hubspot.com/contacts/20258191/record/0-1/${hubspot_id}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden" }} src="/images/HS_logo.jpeg" />
                    </a>
                  </Tooltip>
                </Grid>)}
                {imdb_id && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    IMDB
                  </Typography>
                  <Tooltip title="IMDB">
                    <a target="_blank" style={{ display: "flex" }} href={`https://www.imdb.com/name/${imdb_id}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden",   }} src="/images/imdb.png" />
                    </a>
                  </Tooltip>
                </Grid>)} */}
                {person_master && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    Person Master
                  </Typography>

                  <Tooltip title="Person Master">
                    <a target="_blank" style={{ display: "flex" }} href={`${DELI_ENV_BASE_URL}/person-management/edit/${person_master}`}>
                      <img style={{ height: "30px", width: "30px", borderRadius: "4px", overflow: "hidden",   }} src="/images/vitrina.png" />
                    </a>
                  </Tooltip>

                </Grid>)}
              </Grid>
            </FormDiv>
          </form>
        </FormProvider>
      </div>
    </>
  );
}

export const CellForContentEntity = ({ contentName, contentVtrId,contentCharacter }: { contentName: string, contentVtrId: string,contentCharacter:string }) => {

  if (!contentVtrId) {
    return null;
  }
  const firstEntityName = contentName;
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Content Name
      </Typography>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {firstEntityName} {/* VTR ID with clipboard */}
        {contentVtrId && (
          <ClipBoard
            parentValue={
              <Typography variant="caption" color="primary">
                ID
              </Typography>
            }
            icon={
              <CopyClipboard
                fontSize="small"
                style={{ fontSize: '10px !important' }}
                color="primary"
              />
            }
            value={contentVtrId}
          />
        )}
      </Typography>
      {contentCharacter && (
        <Typography variant="body2" gutterBottom>
          <span style={{ fontWeight: 'bold' }}>Character:</span> {contentCharacter}
        </Typography>
      )}
      {/*  <Typography variant="caption" color="textSecondary">
          ID: {contentVtrId}
        </Typography> */}
    </Box>
  );
};

const FormDiv = styled.div`
  padding: 30px 10%;
`;

const CountryStyles = styled.div`
  .MuiChip-root {
    background: ${blue[600]};
    color: white;
  }
  .MuiChip-deleteIcon {
    color: white;
  }
`;
